import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import "@/index.css";
import { StoreProvider } from "@/store/StoreContext";
import { img } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductPage from "@/pages/ProductPage";
import Collections from "@/pages/Collections";
import Story from "@/pages/Story";
import Lookbook from "@/pages/Lookbook";
import Contact from "@/pages/Contact";
import Account from "@/pages/Account";
import Wishlist from "@/pages/Wishlist";
import Info from "@/pages/Info";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Admin from "@/pages/Admin";
import TrackOrder from "@/pages/TrackOrder";

let lenis = null;

function useLenis() {
  useEffect(() => {
    lenis = new Lenis({ duration: 1.15, autoRaf: true, smoothWheel: true });
    return () => { lenis?.destroy(); lenis = null; };
  }, []);
}

function ScrollReset() {
  const { pathname, search } = useLocation();
  useEffect(() => { lenis?.scrollTo(0, { immediate: true }); }, [pathname, search]);
  return null;
}

function Preloader() {
  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-noir-bg flex flex-col items-center justify-center"
      exit={{ y: "-100%" }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
      data-testid="preloader"
    >
      <motion.img
        src={img("logo-full.png")}
        alt="Jae Noir"
        className="w-40 sm:w-52"
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      />
      <motion.span
        className="mt-5 font-mono2 text-[10px] tracking-[0.4em] text-gold uppercase"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.6 }}
      >
        Move Different. Leave a Legacy.
      </motion.span>
    </motion.div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.main
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:slug" element={<ProductPage />} />
          <Route path="/collections" element={<Collections />} />
          <Route path="/story" element={<Story />} />
          <Route path="/lookbook" element={<Lookbook />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/account" element={<Account />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/info" element={<Info />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/track" element={<TrackOrder />} />
        </Routes>
      </motion.main>
    </AnimatePresence>
  );
}

function Shell() {
  useLenis();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1700);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-noir-bg text-cream font-body">
      <AnimatePresence>{loading && <Preloader />}</AnimatePresence>
      <div className="noise-overlay" />
      <ScrollReset />
      <Nav />
      <AnimatedRoutes />
      <Footer />
      <CartDrawer />
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#111", border: "1px solid #262626", color: "#EAE6DF" } }} />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <StoreProvider>
        <Shell />
      </StoreProvider>
    </BrowserRouter>
  );
}
